import { Component, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { MatTableModule, MatTableDataSource } from "@angular/material/table";
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { WindowRef } from "../../utils/window";
import { TranslateUtil } from "../../utils/translate-util";
import { DTFHomepageService } from "../dtfhomepage/dtfhomepage.service";
import { Banner } from "../../shared/models/banner";
import { MatIconModule } from "@angular/material/icon";
import { MatSort, MatSortModule, Sort } from "@angular/material/sort";
import { SharedTranslateModule } from "../../../../src/app/utils/sharedTranslateModule";
import { AuthorizationService } from '../authorization/authorization.service';
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatPaginator } from "@angular/material/paginator";
import { MatSelectModule } from "@angular/material/select";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { SessionTimeoutComponent } from "../sessionTimeout/sessionTimeout.component";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { BannerEditDialogComponent } from "./banner-edit-dialog.component";

@Component({
  selector: "div.dtf-adminfunctions",
  standalone: true,
  imports: [SharedTranslateModule,
        MatPaginator, MatSort, MatTableModule, MatInputModule, MatIconModule, SessionTimeoutComponent, MatCheckboxModule, MatFormFieldModule,
        MatInputModule, FormsModule, MatSelectModule, ReactiveFormsModule, MatButtonToggleModule, MatSortModule,
        MatDatepickerModule, MatNativeDateModule, MatDialogModule],
  templateUrl: "./adminfunctions.component.html",
  styleUrls: ["./adminfunctions.component.scss"],
})
export class AdminFunctionsComponent implements OnInit {

  alerts: Banner[] = [];
  tableLoaded = true;
  successMessage: string = '';
  dataSource = new MatTableDataSource<Banner>(this.alerts);
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private router: Router,
    public translate: TranslateService,
    private windowRef: WindowRef,
    public translateUtil: TranslateUtil,
    public dtfhomepageService: DTFHomepageService,
    public auth: AuthorizationService,
    private dialog: MatDialog
  ) {
    this.router = router;
    this.alerts = [];
    this.dataSource = new MatTableDataSource<Banner>(this.alerts);
  }

  initColumns: any[] = [
    { name: 'bannerType', display: 'Type', dispFormat: ''},
    { name: 'bannerCountry', display: 'Country', dispFormat: ''},
    { name: 'bannerHeader', display: 'Header', dispFormat: 'editable'},
    { name: 'bannerCopy', display: 'Copy', dispFormat: 'editablecopy'},
    { name: 'bannerEnable', display: 'Enabled', dispFormat: 'editable'},
    { name: 'startDate', display: 'Start Date', dispFormat: 'datepicker'},
    { name: 'endDate', display: 'End Date', dispFormat: 'datepicker'},
  ];
  displayedColumns: any[] = this.initColumns.map(col => col.name);

  ngOnInit(): void {
    this.loadAlerts();
  }

  private loadAlerts(): void {
    this.tableLoaded = false; // Show loading spinner
    this.dtfhomepageService.getAlertsAdmin().subscribe({
      next: (res: Banner[]) => {
        this.alerts = res;
        this.dataSource.data = res; 
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.tableLoaded = true; // Hide loading spinner
      },
      error: (err) => {
        console.error("Failed to load alerts", err);
      }
    });
  }

  toDateTimeLocalValue(value: string | Date | null | undefined): string {
    if (this.isEmptyDateValue(value)) {
      return '';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  onDateTimeChange(element: Banner, fieldName: string, value: string): void {
    if (!value) {
      (element as any)[fieldName] = '';
      return;
    }

    const updatedDate = new Date(value);
    (element as any)[fieldName] = Number.isNaN(updatedDate.getTime()) ? value : updatedDate.toISOString();
  }

  private isEmptyDateValue(value: string | Date | null | undefined): boolean {
    if (!value) {
      return true;
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return true;
      }

      // Backend sentinel values for "empty" dates should be treated as blank.
      if (trimmedValue.startsWith('0001-01-01') || trimmedValue.startsWith('0001-07-30')) {
        return true;
      }
    }

    return false;
  }

  private normalizeDateForSave(value: string | Date | null | undefined): string {
    if (this.isEmptyDateValue(value)) {
      return '';
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? '' : value.toISOString();
    }

    return value;
  }

  saveData() {
  this.dataSource._updateChangeSubscription();

  const updatedData = this.dataSource.data.map((banner) => ({
    ...banner,
    startDate: this.normalizeDateForSave(banner.startDate),
    endDate: this.normalizeDateForSave(banner.endDate),
  }));
  console.log('Saving data:', updatedData);

  this.auth.saveBannerData(updatedData).subscribe({
    next: (res) => {
      console.log('Data saved successfully', res);

      this.successMessage = 'Changes have been saved successfully.';
      
      // Optional: auto-hide after 3 seconds
      setTimeout(() => {
        this.successMessage = '';
      }, 3000);
    },
    error: () => {
      this.successMessage = 'Something went wrong while saving.';
    }
  });
}

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  sortData(sort: Sort): void {
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = [...this.alerts];
      return;
    }
    this.dataSource.data = [...this.dataSource.data].sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      const valA = (a as any)[sort.active] ?? '';
      const valB = (b as any)[sort.active] ?? '';
      return (valA < valB ? -1 : valA > valB ? 1 : 0) * (isAsc ? 1 : -1);
    });
  }

  editBanner(banner: Banner): void {
    const dialogRef = this.dialog.open(BannerEditDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      data: { 
        banner,
        saveCallback: () => this.saveData()
      },
      panelClass: 'banner-edit-dialog-panel'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Update the banner in the dataSource
        const index = this.dataSource.data.indexOf(banner);
        if (index >= 0) {
          this.dataSource.data[index] = { ...banner, ...result };
          this.dataSource._updateChangeSubscription();
        }
      }
    });
  }
}
